import {
  Box,
  Button,
  Drawer,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import backofficeApi from "../utilities/backofficeApi";
import locationApi from "../services/locationApi";
import { getUserdetails, messageServeur } from "../utilities/userService";

import { useGiftform } from "../custom_forms/AchievementForm";
import { loaderOff, loaderOn, logout, openSnackbar } from "../redux/actions";
import {
  creategift,
  getAchievementsActive,
  getactivegifts,
  getgiftbyid,
  getgifts,
  updategift,
  updategifts,
} from "../utilities/backofficeservice";
import { getUser } from "../utilities/adminservice";
import { useValeurTip } from "../utilities/valeurTip";
import CommonDialog from "../commondialog/confirm";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import store from "../redux/store";

// ─── Design system ───────────────────────────────────────────────────────────
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: "var(--c-bg)",
  card: "var(--c-surface)",
  border: "var(--c-border)",
  borderAccent: "#1F1E1B",
  accent: "#1F1E1B",
  accentLight: "#57534A",
  accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B",
  textSub: "#57534A",
  textMuted: "#57534A",
  success: "#356A0F",
  warning: "#7C5600",
  error: "#A23A0D",
} as const;

// ─── Reusable MUI sx helpers ─────────────────────────────────────────────────
const fieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    background: "rgba(31,30,27,0.03)",
    color: C.text,
    borderRadius: "8px",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.borderAccent },
    "&.Mui-focused fieldset": { borderColor: C.accentLight },
  },
  "& .MuiInputLabel-root": { color: C.textMuted },
  "& .MuiInputLabel-root.Mui-focused": { color: C.accentLight },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    { WebkitAppearance: "none" },
};

const thSx = {
  background: C.card,
  color: C.accentLight,
  fontWeight: 700,
  fontSize: "11px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.08em",
  borderBottom: `1px solid ${C.border}`,
};

const tdSx = {
  color: C.text,
  borderBottom: `1px solid ${C.border}`,
};

// ─── Interfaces ───────────────────────────────────────────────────────────────
interface GiftRow {
  Gift: string;
  Gift_Description: string;
  Gift_Criteria: string;
  gift_id: number;
  no_of_tokens: string;
  delete_status?: string;
}

/* Un palier = un badge qui offre les Tips d'un cadeau quand le cumul de Tips
   gagnés (token_allocated) d'un Spotter atteint son seuil (décision D9). */
interface Palier {
  achievement_id: number;
  achievement_name: string;
  seuil: number;
  gift_id: number | null;
}

interface CumulSpotter {
  userId: string;
  cumul: number;
}

/* Supprimé = delete_status autre que « NO » : ni listé, ni compté. */
function nonSupprime(deleteStatus?: string | null): boolean {
  return deleteStatus == null || deleteStatus.trim().toUpperCase() === "NO";
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function cfaLabel(tips: number, rate: number | null): string {
  return rate == null ? "— F CFA" : Math.round(tips * rate).toLocaleString("fr-FR") + " F CFA";
}

// ─── Component ────────────────────────────────────────────────────────────────
/* `integre` : rendu comme section de l'écran Niveaux & Badges (option 3),
   sans en-tête de page ni fond plein écran. */
export default function Gifts({ integre = false }: { integre?: boolean } = {}) {
  const dispatch   = useDispatch();
  const navigate   = useNavigate();
  const isLoggedIn     = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);


  // Form
  const { formValues, setFormValues } = useGiftform();

  // Data
  const [activeGifts, setActiveGifts] = useState<GiftRow[]>([]);
  const [allGifts,    setAllGifts]    = useState<GiftRow[]>([]);
  // Valeur officielle du Tip (décision D1), jamais de valeur par défaut en dur.
  const conversionRate = useValeurTip();
  const [paliers, setPaliers] = useState<Palier[]>([]);
  const [cumuls,  setCumuls]  = useState<CumulSpotter[]>([]);

  // Permissions
  const [giftsWrite, setGiftsWrite] = useState(false);

  // Table pagination
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId,  setEditingId]  = useState<number | null>(null);

  // Delete dialog
  const [openDialog,      setOpenDialog]      = useState(false);
  const [deleteTargetId,  setDeleteTargetId]  = useState<number | null>(null);

  // Per-row push state
  const [sendingPush, setSendingPush] = useState<Record<number, boolean>>({});
  const [pushConfirm, setPushConfirm] = useState<Record<number, string>>({});

  // History collapse
  const [showHistory, setShowHistory] = useState(false);
  const [histPage,    setHistPage]    = useState(0);
  const histRowsPerPage = 10;

  // ── Form helpers ────────────────────────────────────────────────────────────
  const resetForm = () => {
    setFormValues({
      gift_name:        { value: "", error: false, errorMessage: "Please enter valid Gift name" },
      gift_description: { value: "", error: false, errorMessage: "Please enter valid Gift description" },
      gift_criteria:    { value: "", error: false, errorMessage: "Please enter valid Gift criteria" },
      no_of_tokens:     { value: "", error: false, errorMessage: "Please enter a valid number of Tips" },
    });
    setEditingId(null);
  };

  const closeDrawer  = () => { setDrawerOpen(false); resetForm(); };
  const openAddDrawer = () => { resetForm(); setDrawerOpen(true); };

  // ── Data fetching ────────────────────────────────────────────────────────────
  const fetchActiveGifts = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getactivegifts();
      if (Array.isArray(res)) {
        setActiveGifts(
          res.filter((r: any) => nonSupprime(r.delete_status)).map((r: any) => ({
            Gift:             r.gift_name        || "",
            Gift_Description: r.gift_description || "",
            Gift_Criteria:    r.gift_criteria     || "",
            gift_id:          r.gift_id,
            no_of_tokens:     String(r.no_of_tokens ?? ""),
            delete_status:    r.delete_status,
          }))
        );
      }
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Cadeaux en Tips illisibles"), "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  const fetchAllGifts = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getgifts();
      if (Array.isArray(res)) {
        setAllGifts(
          res.filter((r: any) => nonSupprime(r.delete_status)).map((r: any) => ({
            Gift:             r.gift_name        || "",
            Gift_Description: r.gift_description || "",
            Gift_Criteria:    r.gift_criteria     || "",
            gift_id:          r.gift_id,
            no_of_tokens:     String(r.no_of_tokens ?? ""),
            delete_status:    r.delete_status,
          }))
        );
      }
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Historique des cadeaux en Tips illisible"), "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  /* Les paliers (badges non supprimés) et le cumul de Tips gagnés de chaque
     Spotter, lu sur le solde serveur (token_allocated de /user/all) — pas une
     somme des POI, qui ignorait missions, corrections et paliers déjà payés. */
  const fetchPaliersEtCumuls = async () => {
    try {
      const [badges, users] = await Promise.all([getAchievementsActive(), getUserdetails()]);
      setPaliers(
        (Array.isArray(badges) ? badges : [])
          .filter((b: any) => nonSupprime(b.delete_status) && b.gift_name && nonSupprime(b.gift_name.delete_status))
          .map((b: any) => ({
            achievement_id:   b.achievement_id,
            achievement_name: b.achievement_name || "",
            seuil:            Number(b.no_of_tokens) || 0,
            gift_id:          b.gift_name?.gift_id ?? null,
          }))
      );
      setCumuls(
        (Array.isArray(users) ? users : [])
          .map((u: any) => ({
            userId: String(u.user_id ?? u.userId ?? u.id ?? ""),
            cumul:  Number(u.token_allocated) || 0,
          }))
          .filter((u: CumulSpotter) => u.userId)
      );
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Paliers (badges) illisibles"), "red"));
    }
  };

  const paliersDuCadeau = (giftId: number) => paliers.filter((p) => p.gift_id === giftId);
  /* Spotters dont le cumul atteint le plus bas seuil des paliers donnés. */
  const spottersAuSeuil = (liste: Palier[]): CumulSpotter[] => {
    if (liste.length === 0) return [];
    const seuilMin = Math.min(...liste.map((p) => p.seuil));
    return cumuls.filter((c) => c.cumul >= seuilMin);
  };

  const getRoleData = async () => {
    try {
      const res: any = await getUser();
      setGiftsWrite(res?.role_type?.gifts_write ?? false);
    } catch (_) {
      dispatch(openSnackbar("Droits de l'administrateur illisibles : modification des cadeaux en Tips masquée", "red"));
    }
  };

  useEffect(() => {
    fetchActiveGifts();
    fetchAllGifts();
    getRoleData();
  }, []);

  useEffect(() => { fetchPaliersEtCumuls(); }, []);

  const idsCadeauxActifs = new Set(activeGifts.map((g) => g.gift_id));
  const eligibleCount = spottersAuSeuil(paliers.filter((p) => p.gift_id != null && idsCadeauxActifs.has(p.gift_id))).length;

  // ── CRUD ─────────────────────────────────────────────────────────────────────
  const handleAddGift = async () => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session expirée", "red")); return; }
    if (!formValues.gift_name.value.trim()) {
      dispatch(openSnackbar("Le nom est obligatoire", "red")); return;
    }
    const tokensNum = parseFloat(String(formValues.no_of_tokens.value));
    if (!formValues.no_of_tokens.value || isNaN(tokensNum) || tokensNum <= 0) {
      dispatch(openSnackbar("Le nombre de Tips doit être supérieur à 0", "red")); return;
    }
    const formData = {
      gift_name:        formValues.gift_name.value,
      gift_description: formValues.gift_description.value,
      gift_criteria:    formValues.gift_criteria.value || "To allocate to user completing achievements",
      no_of_tokens:     tokensNum,
      delete_status:    "NO",
    };
    dispatch(loaderOn());
    try {
      await creategift(formData);
      closeDrawer();
      fetchActiveGifts();
      fetchAllGifts();
      dispatch(openSnackbar("Cadeau en Tips créé", "green"));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Création du cadeau en Tips impossible"), "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleOpenEdit = async (giftId: number) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      const res = await getgiftbyid(giftId);
      setFormValues((prev) => ({
        ...prev,
        gift_name:        { ...prev.gift_name,        value: res.gift_name        || "" },
        gift_description: { ...prev.gift_description, value: res.gift_description || "" },
        gift_criteria:    { ...prev.gift_criteria,    value: res.gift_criteria    || "" },
        no_of_tokens:     { ...prev.no_of_tokens,     value: String(res.no_of_tokens || "") },
      }));
      setEditingId(giftId);
      setDrawerOpen(true);
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Cadeau en Tips illisible"), "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleUpdateGift = async (giftId: number) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    const formData = {
      gift_name:        formValues.gift_name.value,
      gift_description: formValues.gift_description.value,
      gift_criteria:    formValues.gift_criteria.value || "To allocate to user completing achievements",
      no_of_tokens:     formValues.no_of_tokens.value || 0,
    };
    dispatch(loaderOn());
    try {
      await updategift(giftId, formData);
      closeDrawer();
      fetchActiveGifts();
      fetchAllGifts();
      dispatch(openSnackbar("Cadeau en Tips mis à jour", "green"));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Mise à jour du cadeau en Tips impossible"), "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  const handleDeleteGift = async (giftId: number) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) return;
    dispatch(loaderOn());
    try {
      await updategifts(giftId, { delete_status: "Yes" });
      fetchActiveGifts();
      fetchAllGifts();
      dispatch(openSnackbar("Cadeau en Tips supprimé : ses paliers n'offrent plus de Tips", "green"));
    } catch (err) {
      dispatch(openSnackbar(messageServeur(err, "Suppression du cadeau en Tips impossible"), "red"));
    } finally {
      dispatch(loaderOff());
    }
  };

  // ── Manual push ──────────────────────────────────────────────────────────────
  const handleManualPush = async (gift: GiftRow) => {
    const jwtToken = sessionStorage.getItem("jwtToken");
    if (!jwtToken) { dispatch(openSnackbar("Session expirée", "red")); return; }
    if (!window.confirm(`Envoyer le push « ${gift.Gift} » aux Spotters qui ont franchi un de ses paliers ?`)) return;
    setSendingPush((p) => ({ ...p, [gift.gift_id]: true }));
    try {
      // Spotters ayant franchi un palier de ce cadeau : le serveur leur a déjà
      // crédité les Tips offerts, le push ne fait que le leur dire.
      const eligibleIds: string[] = spottersAuSeuil(paliersDuCadeau(gift.gift_id)).map((c) => c.userId);
      if (eligibleIds.length === 0) {
        dispatch(openSnackbar("Aucun Spotter n'a encore franchi un palier de ce cadeau.", "red"));
        return;
      }
      await locationApi.post('/notification/send', {
        titre:   "🎁 Tips offerts au palier",
        corps:   `${gift.Gift} : ${gift.no_of_tokens} Tips offerts, crédités automatiquement sur ton solde au passage du palier.`,
        cible:   "users",
        userIds: eligibleIds,
      });
      const msg = `Push envoyé à ${eligibleIds.length} Spotter${eligibleIds.length !== 1 ? "s" : ""} éligible${eligibleIds.length !== 1 ? "s" : ""}`;
      setPushConfirm((p) => ({ ...p, [gift.gift_id]: msg }));
      setTimeout(
        () =>
          setPushConfirm((p) => {
            const n = { ...p };
            delete n[gift.gift_id];
            return n;
          }),
        6000
      );
    } catch (_) {
      dispatch(openSnackbar("Erreur lors de l'envoi du push", "red"));
    } finally {
      setSendingPush((p) => ({ ...p, [gift.gift_id]: false }));
    }
  };

  // ── Derived values ───────────────────────────────────────────────────────────
  const tipsInput = parseFloat(formValues.no_of_tokens.value) || 0;
  const cfaPreview = tipsInput > 0 ? cfaLabel(tipsInput, conversionRate) : null;

  const pageSlice = activeGifts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const histSlice = allGifts.slice(
    histPage * histRowsPerPage,
    histPage * histRowsPerPage + histRowsPerPage
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Box sx={integre ? {} : { p: 3, minHeight: "100vh", background: C.bg }}>

      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 4,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          {!integre && (
            <Typography
              sx={{ fontSize: 24, fontWeight: 700, color: C.text, letterSpacing: "-0.02em" }}
            >
              Tips offerts au palier
            </Typography>
          )}
          <Typography sx={{ fontSize: 13, color: C.textMuted, mt: integre ? 0 : 0.5, maxWidth: 640 }}>
            Quand le cumul de Tips gagnés d'un Spotter atteint le seuil d'un badge, le serveur lui crédite
            automatiquement les Tips du cadeau lié, une seule fois — sauf si le badge ou le cadeau est supprimé.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          {giftsWrite && (
            <Button
              startIcon={<AddIcon />}
              onClick={openAddDrawer}
              sx={{
                background: C.accent,
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 3,
                py: 1.2,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              Ajouter un cadeau en Tips
            </Button>
          )}
        </Box>
      </Box>

      {/* ── Main active-gifts table ───────────────────────────────────────────── */}
      <Box
        sx={{
          background: C.card,
          border: "1.5px solid #1F1E1B",
          borderRadius: "8px",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: `1px solid ${C.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 600,
              color: C.textSub,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
            }}
          >
            Cadeaux en Tips ({activeGifts.length})
          </Typography>
          <Box
            sx={{
              fontSize: 12,
              color: C.textMuted,
              background: "rgba(31,30,27,0.04)",
              px: 1.5,
              py: 0.5,
              borderRadius: "6px",
            }}
          >
            1 Tip = {conversionRate ?? "—"} F CFA
          </Box>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={thSx}>Nom</TableCell>
                <TableCell sx={thSx}>Paliers (badges)</TableCell>
                <TableCell sx={thSx}>Tips offerts</TableCell>
                <TableCell sx={thSx}>Push manuel</TableCell>
                {giftsWrite && <TableCell sx={thSx}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {pageSlice.map((row) => {
                const sesPaliers = paliersDuCadeau(row.gift_id);
                const tips    = parseFloat(row.no_of_tokens) || 0;
                const cfaStr  = cfaLabel(tips, conversionRate);
                const pushing = sendingPush[row.gift_id] ?? false;
                const confirm = pushConfirm[row.gift_id];

                return (
                  <TableRow
                    key={row.gift_id}
                    sx={{
                      "&:hover": { background: "rgba(31,30,27,0.02)" },
                      "& .MuiTableCell-root": tdSx,
                    }}
                  >
                    {/* Icône + Nom + Description */}
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: "8px",
                            background: C.accentMuted,
                            border: `1px solid ${C.borderAccent}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 22,
                            flexShrink: 0,
                          }}
                        >
                          🎁
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                            {row.Gift}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.2 }}>
                            {row.Gift_Description}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Paliers qui offrent ce cadeau */}
                    <TableCell>
                      {sesPaliers.length === 0 ? (
                        <Typography sx={{ fontSize: 11, color: C.textMuted, fontStyle: "italic" }}>
                          Aucun badge lié : rien n'est offert
                        </Typography>
                      ) : sesPaliers.map((p) => (
                        <Typography key={p.achievement_id} sx={{ fontSize: 11, color: C.textSub, whiteSpace: "nowrap" }}>
                          {p.achievement_name} · dès {p.seuil.toLocaleString("fr-FR")} Tips cumulés
                          {" · "}{cumuls.filter((c) => c.cumul >= p.seuil).length} Spotters
                        </Typography>
                      ))}
                    </TableCell>

                    {/* Tips + CFA */}
                    <TableCell>
                      <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.warning }}>
                        {row.no_of_tokens} Tips
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.2 }}>
                        = {cfaStr}
                      </Typography>
                    </TableCell>

                    {/* Push manuel */}
                    <TableCell>
                      {confirm ? (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            color: C.success,
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />
                          {confirm}
                        </Box>
                      ) : (
                        <Tooltip title="Envoyer une notification push aux Spotters éligibles">
                          <span>
                            <Button
                              size="small"
                              startIcon={<NotificationsActiveIcon sx={{ fontSize: 14 }} />}
                              disabled={pushing}
                              onClick={() => handleManualPush(row)}
                              sx={{
                                fontSize: 11,
                                fontWeight: 600,
                                textTransform: "none",
                                color: C.accentLight,
                                border: `1px solid ${C.borderAccent}`,
                                borderRadius: "7px",
                                px: 1.5,
                                py: 0.5,
                                background: C.accentMuted,
                                "&:hover": {
                                  background: `${C.accent}33`,
                                  borderColor: C.accentLight,
                                },
                                "&:disabled": { opacity: 0.5 },
                              }}
                            >
                              {pushing ? "Envoi…" : "Push manuel"}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </TableCell>

                    {/* Actions */}
                    {giftsWrite && (
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Modifier">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenEdit(row.gift_id)}
                              sx={{
                                color: C.accentLight,
                                "&:hover": { background: C.accentMuted },
                              }}
                            >
                              <CreateIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setDeleteTargetId(row.gift_id);
                                setOpenDialog(true);
                              }}
                              sx={{
                                color: C.error,
                                "&:hover": { background: "rgba(162,58,13,0.1)" },
                              }}
                            >
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}

              {activeGifts.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={giftsWrite ? 5 : 4}
                    sx={{ textAlign: "center", py: 6, color: C.textMuted, borderBottom: "none" }}
                  >
                    <Typography sx={{ fontSize: 36, mb: 1 }}>🎁</Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                      Aucun cadeau en Tips
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.5 }}>
                      Créez un cadeau, puis liez-le à un badge pour qu'un palier l'offre
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={activeGifts.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_e, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{
            color: C.textSub,
            borderTop: `1px solid ${C.border}`,
            "& .MuiSvgIcon-root": { color: C.textMuted },
          }}
        />
      </Box>

      {/* ── History (all gifts, collapsible) ─────────────────────────────────── */}
      <Box
        sx={{
          background: C.card,
          border: "1.5px solid #1F1E1B",
          borderRadius: "8px",
          overflow: "hidden",
          mb: 3,
        }}
      >
        <Box
          onClick={() => setShowHistory((p) => !p)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 3,
            py: 2,
            cursor: "pointer",
            userSelect: "none",
            "&:hover": { background: "rgba(31,30,27,0.02)" },
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: C.textSub }}>
            Historique complet ({allGifts.length})
          </Typography>
          <Typography sx={{ fontSize: 12, color: C.textMuted }}>
            {showHistory ? "▲ Réduire" : "▼ Afficher"}
          </Typography>
        </Box>

        {showHistory && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["#", "Nom", "Description", "Critère", "Tips offerts"].map(
                      (h) => (
                        <TableCell key={h} sx={thSx}>
                          {h}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {histSlice.map((row, idx) => {
                    return (
                      <TableRow
                        key={row.gift_id}
                        sx={{
                          "&:hover": { background: "rgba(31,30,27,0.02)" },
                          "& .MuiTableCell-root": { ...tdSx, fontSize: 13 },
                        }}
                      >
                        <TableCell sx={{ color: C.textMuted, width: 40 }}>
                          {histPage * histRowsPerPage + idx + 1}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                            {row.Gift}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ color: C.textMuted, maxWidth: 200 }}>
                          {row.Gift_Description}
                        </TableCell>
                        <TableCell sx={{ color: C.textMuted }}>{row.Gift_Criteria}</TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.warning }}>
                            {row.no_of_tokens}
                          </Typography>
                          <Typography sx={{ fontSize: 10, color: C.textMuted }}>
                            = {cfaLabel(parseFloat(row.no_of_tokens) || 0, conversionRate)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {allGifts.length === 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{ textAlign: "center", py: 4, color: C.textMuted, borderBottom: "none" }}
                      >
                        Aucune donnée
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {allGifts.length > histRowsPerPage && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  px: 2,
                  py: 1,
                  borderTop: `1px solid ${C.border}`,
                }}
              >
                <Typography sx={{ fontSize: 12, color: C.textMuted, mr: 2 }}>
                  {histPage * histRowsPerPage + 1}–
                  {Math.min((histPage + 1) * histRowsPerPage, allGifts.length)} sur{" "}
                  {allGifts.length}
                </Typography>
                <Button
                  size="small"
                  disabled={histPage === 0}
                  onClick={() => setHistPage((p) => p - 1)}
                  sx={{ color: C.textMuted, minWidth: 32 }}
                >
                  ‹
                </Button>
                <Button
                  size="small"
                  disabled={(histPage + 1) * histRowsPerPage >= allGifts.length}
                  onClick={() => setHistPage((p) => p + 1)}
                  sx={{ color: C.textMuted, minWidth: 32 }}
                >
                  ›
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>

      {/* ── Footer banners ───────────────────────────────────────────────────── */}
      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {/* Banner 1: active gifts · eligible collectors */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: C.card,
            border: "1.5px solid #1F1E1B",
            borderRadius: "8px",
            px: 2.5,
            py: 1.25,
            flex: "1 1 auto",
          }}
        >
          <Typography sx={{ fontSize: 18 }}>🎁</Typography>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              {activeGifts.length} cadeau{activeGifts.length !== 1 ? "x" : ""} en Tips · {eligibleCount} Spotter
              {eligibleCount !== 1 ? "s" : ""} au-dessus du premier palier
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted }}>
              Cumul de Tips gagnés lu sur le solde serveur, comparé au plus bas seuil des badges liés
            </Typography>
          </Box>
        </Box>

        {/* Banner 2: conversion rate */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            background: C.card,
            border: "1.5px solid #1F1E1B",
            borderRadius: "8px",
            px: 2.5,
            py: 1.25,
          }}
        >
          <Typography sx={{ fontSize: 18 }}>💱</Typography>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
              Taux de conversion : 1 Tip = {conversionRate ?? "—"} F CFA
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted }}>
              Valeur officielle du Tip, réglée dans Configuration
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* ── Delete confirm dialog ────────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        handleClose={() => setOpenDialog(false)}
        handleConfirm={() => {
          if (deleteTargetId !== null) handleDeleteGift(deleteTargetId);
          setOpenDialog(false);
        }}
        contentText="Supprimer ce cadeau ? Les badges qui le lient n'offriront plus de Tips."
      />

      {/* ── Add / Edit Drawer ────────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: 440,
            background: C.card,
            borderLeft: `1px solid ${C.border}`,
          },
        }}
      >
        <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Drawer header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 3,
            }}
          >
            <Box>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text }}>
                {editingId ? "Modifier le cadeau en Tips" : "Ajouter un cadeau en Tips"}
              </Typography>
              <Typography sx={{ fontSize: 12, color: C.textMuted, mt: 0.25 }}>
                Tips offerts au palier, crédités automatiquement
              </Typography>
            </Box>
            <IconButton onClick={closeDrawer} sx={{ color: C.textMuted }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable fields */}
          <Box
            sx={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 2.5 }}
          >

            {/* Nom */}
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textSub,
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Nom du cadeau
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Ex: Bon de réduction Jumia"
                value={formValues.gift_name.value}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    gift_name: { ...prev.gift_name, value: e.target.value },
                  }))
                }
                sx={fieldSx}
              />
            </Box>

            {/* Description */}
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textSub,
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Description
              </Typography>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder="Description du cadeau..."
                value={formValues.gift_description.value}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    gift_description: { ...prev.gift_description, value: e.target.value },
                  }))
                }
                sx={fieldSx}
              />
            </Box>

            {/* Critère */}
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textSub,
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Critère d'attribution
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Ex: 50 collectes approuvées"
                value={formValues.gift_criteria.value}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    gift_criteria: { ...prev.gift_criteria, value: e.target.value },
                  }))
                }
                sx={fieldSx}
              />
            </Box>

            {/* Tips offerts (le seuil est porté par le badge) */}
            <Box>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textSub,
                  mb: 0.75,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Tips offerts
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                placeholder="Ex: 500"
                value={formValues.no_of_tokens.value}
                onChange={(e) =>
                  setFormValues((prev) => ({
                    ...prev,
                    no_of_tokens: { ...prev.no_of_tokens, value: e.target.value },
                  }))
                }
                sx={fieldSx}
              />
              {cfaPreview && (
                <Typography sx={{ fontSize: 12, color: C.warning, mt: 0.75, fontWeight: 600 }}>
                  = {cfaPreview}
                </Typography>
              )}
              <Typography sx={{ fontSize: 11, color: C.textMuted, mt: 0.5 }}>
                Le seuil de Tips cumulés se règle sur le badge qui lie ce cadeau.
              </Typography>
            </Box>

            {/* Live preview */}
            <Box
              sx={{
                background: C.accentMuted,
                border: `1px solid ${C.borderAccent}`,
                borderRadius: "8px",
                p: 1.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  mb: 1,
                }}
              >
                Aperçu
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: "8px",
                    background: C.card,
                    border: `1px solid ${C.borderAccent}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 24,
                    flexShrink: 0,
                  }}
                >
                  🎁
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text }}>
                    {formValues.gift_name.value || "Nom du cadeau"}
                  </Typography>
                  {tipsInput > 0 && (
                    <Typography sx={{ fontSize: 12, color: C.warning, mt: 0.25 }}>
                      {tipsInput} Tips{cfaPreview ? ` = ${cfaPreview}` : ""}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Drawer footer actions */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              pt: 3,
              mt: 2,
              borderTop: `1px solid ${C.border}`,
            }}
          >
            <Button
              fullWidth
              onClick={closeDrawer}
              sx={{
                color: C.textSub,
                border: `1px solid ${C.border}`,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { background: "rgba(31,30,27,0.04)" },
              }}
            >
              Annuler
            </Button>
            <Button
              fullWidth
              onClick={() => {
                if (editingId) handleUpdateGift(editingId);
                else handleAddGift();
              }}
              sx={{
                background: C.accent,
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { boxShadow: "none" },
              }}
            >
              {editingId ? "Mettre à jour" : "Ajouter"}
            </Button>
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
}
